import React, { useEffect, useMemo, useState } from "react";
import api from "../../chat/api";
import CopilotMessage from "./CopilotMessage";
import { CopilotAvatar } from "./CopilotMark";
import { CopilotReasoningSummary } from "./CopilotReasoning";
import CopilotQualifyCard, { qualifySummaryText } from "./CopilotQualifyCard";
import CopilotRecallCard, { recallSummaryText } from "./CopilotRecallCard";
import CopilotApprovalCard from "./CopilotApprovalCard";
import CopilotRequirementsCard from "./CopilotRequirementsCard";
import CopilotTenderResultsCard from "./CopilotTenderResultsCard";
import ProposalSetupCard from "./ProposalSetupCard";
import ProposalStepRow from "./ProposalStepRow";
import ProposalPlanCard from "./ProposalPlanCard";
import ProposalOutlineCard from "./ProposalOutlineCard";
import ProposalApproachCard from "./ProposalApproachCard";
import ProposalClarifyCard from "./ProposalClarifyCard";
import ProposalOneGoCard from "./ProposalOneGoCard";
import ProposalWritingCard from "./ProposalWritingCard";
import ProposalSectionCard from "./ProposalSectionCard";
import ProposalCompleteCard from "./ProposalCompleteCard";
import ProposalTemplateCard from "./ProposalTemplateCard";

/* One turn in the conversation.
 *
 * The assistant deliberately has NO bubble. Wrapping a reply in a grey rounded
 * rectangle works for a sentence and fails for everything this copilot actually
 * returns — tables, lists, multi-paragraph analysis. The box fights the content,
 * caps its width, and makes a considered answer look like a chat blurb.
 *
 * So: the user speaks in a bubble (short, and the alignment marks whose turn it
 * is), the assistant speaks on the page (avatar + name give it identity without
 * boxing it in). This is the shape every serious assistant UI converges on, for
 * this reason.
 */

/* Who is speaking.
 *
 * Every branch that OPENS an utterance draws this, so it lives in one place
 * rather than in five copies that drift. The plain-reply branch below keeps its
 * own arrangement — there the avatar is a column beside the text rather than a
 * line above it — but everything card-shaped uses this.
 */
const SensaHeader = () => (
  <div className="mb-1.5 flex items-center gap-3">
    <CopilotAvatar />
    <span className="text-[13px] font-semibold text-[#6B6472]">Sensa</span>
  </div>
);

/* Reveal a line as if it were being written.
 *
 * The flow's narration is known in full the moment it is posted — it comes from
 * the page, not the model — and dropping a finished sentence on screen in one
 * frame reads as a label appearing rather than something being said.
 *
 * ONLY for messages flagged `typed`. Real replies already arrive a token at a
 * time, and restored history must never re-type itself. */
const TYPE_MS = 14;
const TYPE_STEP = 2;

const useTypedText = (text, enabled) => {
  const full = String(text || "");
  const [n, setN] = useState(() => (enabled ? 0 : full.length));

  useEffect(() => {
    if (!enabled || n >= full.length) return undefined;
    // Anyone who asked for less motion gets the sentence, not the performance.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setN(full.length);
      return undefined;
    }
    const t = setTimeout(() => setN((v) => Math.min(full.length, v + TYPE_STEP)), TYPE_MS);
    return () => clearTimeout(t);
  }, [enabled, n, full]);

  return enabled ? full.slice(0, n) : full;
};

/* Square icon buttons, 28px — above the 24px minimum target size, so losing the
 * labels costs nothing in reach. Each carries its name in aria-label and title
 * instead, which is where a one-word label was doing its work anyway. */
const ACTION =
  "flex h-7 w-7 items-center justify-center rounded-md transition-colors " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none";

/* Was this answer any good?
 *
 * Recorded against the ids the reply was persisted under, so a rating survives
 * the page. Clicking the same thumb again clears it — a rating you cannot undo
 * is one people stop giving, because the cost of a misclick is permanent.
 */
const Thumbs = ({ messageId, runId, initial }) => {
  const [vote, setVote] = useState(initial || null);
  const [failed, setFailed] = useState(false);

  if (!messageId || !runId) return null;

  const send = async (type) => {
    const next = vote === type ? null : type;
    const previous = vote;
    setVote(next);           // answer the click immediately; it is a one-bit opinion
    setFailed(false);
    try {
      await api.post("/api/v1/rfp/feedback/", {
        message_id: messageId,
        run_id: runId,
        feedback_type: next || type,
        ...(next ? {} : { user_feedback: "" }),
      });
    } catch {
      setVote(previous);     // put the thumb back rather than lie about it
      setFailed(true);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => send("up")}
        aria-pressed={vote === "up"}
        aria-label="Good response"
        title="Good response"
        className={`${ACTION} ${vote === "up" ? "bg-[#2DAD4E]/10 text-[#1F7D38]" : "text-[#77707E] hover:bg-[#18101C]/[0.05] hover:text-[#3F3F46]"}`}
      >
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill={vote === "up" ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M7 10v11H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1z" />
          <path d="M7 10l4.2-7.3a1 1 0 0 1 1.8.2l.4 1.4a4 4 0 0 1-.2 2.8L12.5 9h5.9a2 2 0 0 1 1.9 2.6l-2.2 7A2 2 0 0 1 16.2 20H7" />
        </svg>
      </button>
      <button
        type="button"
        onClick={() => send("down")}
        aria-pressed={vote === "down"}
        aria-label="Bad response"
        title="Bad response"
        className={`${ACTION} ${vote === "down" ? "bg-[#FEF2F2] text-[#B91C1C]" : "text-[#77707E] hover:bg-[#18101C]/[0.05] hover:text-[#3F3F46]"}`}
      >
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill={vote === "down" ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 14V3h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1z" />
          <path d="M17 14l-4.2 7.3a1 1 0 0 1-1.8-.2l-.4-1.4a4 4 0 0 1 .2-2.8L11.5 15H5.6a2 2 0 0 1-1.9-2.6l2.2-7A2 2 0 0 1 7.8 4H17" />
        </svg>
      </button>
      {failed && <span className="ml-1 text-[12px] text-[#B91C1C]">Couldn't save that</span>}
    </>
  );
};

const CopyButton = ({ text }) => {
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!done) return undefined;
    const t = setTimeout(() => setDone(false), 1600);
    return () => clearTimeout(t);
  }, [done]);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
        } catch {
          /* clipboard blocked — text is selectable, so this is non-fatal */
        }
      }}
      aria-label={done ? "Copied" : "Copy reply"}
      title={done ? "Copied" : "Copy reply"}
      className={`${ACTION} ${done ? "text-[#1F7D38]" : "text-[#77707E] hover:bg-[#18101C]/[0.05] hover:text-[#3F3F46]"}`}
    >
      {/* The tick is the confirmation now that the word is gone — which is the
          faster read anyway; nobody parses "Copied" before believing the tick. */}
      {done ? (
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="9" width="12" height="12" rx="2" />
          <path d="M5 15V5a2 2 0 0 1 2-2h10" />
        </svg>
      )}
    </button>
  );
};

/* Copy and rate, for one assistant message.
 *
 * Extracted because it renders in two places now: under the message when that
 * is the end of the turn, and under the last flow card when one follows. Always
 * visible rather than hover-revealed — rating an answer is only worth asking
 * for at the moment it is read, and a control found by sweeping the mouse is
 * one most people never find. Quiet enough at 11.5px to sit under prose without
 * turning the reply into a card with a toolbar.
 */
const FeedbackRow = ({ message, text }) => (
  <div className="mt-2 -ml-1.5 flex flex-wrap items-center gap-px">
    <CopyButton text={text} />
    <Thumbs messageId={message.messageId} runId={message.runId} initial={message.feedback} />
  </div>
);

const CopilotTurn = ({ message, prev, next, streaming, flow, feedbackFor, opensTurn, liveTrace, onCite, onQualifyRerun, onApprovalRespond, onOpenProject, onOpenAspect, onOpenBand, onOpenTender, onTenderProjectCreated, activeTenderId, onProposalStart, proposalCarry, onProposalCarry }) => {
  const { role, text } = message;

  /* The passages this answer is allowed to cite.
   *
   * Read off the TRACE, because that is where they already live and already
   * persist: the row that remembers "Searched documents" remembers what the
   * search found, so a citation is still clickable in a conversation reopened
   * next week — with no second marker, no schema change, and no way for the
   * two to drift apart.
   *
   * `liveTrace` covers the turn currently streaming, whose trace has not been
   * parked on the message yet. It only changes on tool events, never per token,
   * so this does not rebuild while text lands.
   *
   * Declared before every early return below: hooks must run in the same order
   * on every render, and half the roles here return before this point.
   */
  const sources = useMemo(() => {
    const steps = message.trace?.steps || liveTrace?.steps || [];
    return steps.flatMap((s) => s.sources || []);
  }, [message.trace, liveTrace]);

  // Unconditional, like the memo above: half the roles below return before this
  // point, and a hook that runs for only some of them changes hook order.
  const shown = useTypedText(text, !!message.typed);
  const typing = !!message.typed && shown.length < String(text || "").length;

  /* A narration line that is waiting on something keeps the caret after it has
   * finished typing — otherwise a sentence like "Checking your documents against
   * the RFP" sits there completed for twenty seconds and reads as a hang.
   *
   * Gated on being LAST (`!next`), which is what makes it self-clearing: the
   * moment a card or another line is posted the caret goes, with nothing to
   * reset. `busy` is never persisted, so a restored transcript cannot show a
   * caret for work that finished days ago. */
  const waiting = !!message.busy && !next;

  // A card and the sentence the copilot adds under it are one utterance, not
  // two. Repeating the avatar and the "Sensa" name between them splits a
  // single action into two floating blocks — which is exactly how it read.
  const continuesCard = prev?.role === "approval";
  // A reply under a tenders card shows no trace either: the card is the record
  // of that work (see the tenders row).
  const tracedAboveCard = prev?.role === "tenders";

  /* The qualify card is a REPLY, so it is dressed as one.
   *
   * It used to render bare and flush to the column edge, which put it on a
   * different left margin from every sentence around it and left it unattributed
   * — the one answer in the conversation with no name on it and no way to rate
   * it, despite being the highest-stakes thing the copilot produces. */
  /* Dressed as a reply, like the qualify card: same avatar, same left margin.
   * Its Copy/Thumbs appear once the narrative has landed — while panels are
   * still filling there is nothing stable to copy or to rate. */
  if (role === "recall") {
    return (
      <div className="animate-copilot-rise motion-reduce:animate-none">
        {opensTurn && <SensaHeader />}
        <div className="pl-10">
          <CopilotRecallCard message={message} />
          {message.state === "ready" && (
            <div className="mt-2 -ml-1.5 flex flex-wrap items-center gap-px">
              <CopyButton text={recallSummaryText(message)} />
              <Thumbs
                messageId={message.messageId}
                runId={message.runId}
                initial={message.feedback}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (role === "qualify") {
    return (
      <div className="animate-copilot-rise motion-reduce:animate-none">
        {opensTurn && <SensaHeader />}
        <div className="pl-10">
          <CopilotQualifyCard
            message={message}
            onRerun={() => onQualifyRerun?.(message)}
            onOpenAspect={onOpenAspect}
            onOpenBand={onOpenBand}
          />
          {message.state === "ready" && (
            <div className="mt-2 -ml-1.5 flex flex-wrap items-center gap-px">
              <CopyButton text={qualifySummaryText(message)} />
              <Thumbs
                messageId={message.messageId}
                runId={message.runId}
                initial={message.feedback}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // The approval card DOES get the avatar — unlike the qualify card, it is the
  // copilot acting and speaking, and the reply underneath continues from it.
  //
  // The avatar sits on its OWN row rather than beside the card. Prose has no
  // edges, so the usual side-by-side column works for it; a bordered card put
  // in that column brings its corner up alongside the avatar, and the avatar's
  // drop shadow bleeds across the join — it reads as the two overlapping. A
  // full row above clears it, and pl-10 keeps the card on the same 40px text
  // column (28px avatar + 12px gap) every other assistant turn uses.
  if (role === "approval") {
    return (
      <div className="animate-copilot-rise motion-reduce:animate-none">
        {opensTurn && <SensaHeader />}
        <div className="pl-10">
          <CopilotApprovalCard
            message={message}
            onRespond={(action, toolArgs, submitted) =>
              onApprovalRespond?.(message.id, action, toolArgs, submitted)
            }
            onOpenProject={onOpenProject}
          />
        </div>
      </div>
    );
  }

  // Dressed as a reply, like the qualify card: same avatar, same left margin.
  // A card rendering bare and flush to the column edge is the one answer in the
  // conversation with no name on it.
  if (role === "tenders") {
    // No "Looked up the tender" line once the turn is done: the card already
    // shows what was looked up, so the line only pushed it down the column.
    return (
      <div className="animate-copilot-rise motion-reduce:animate-none">
        {opensTurn && <SensaHeader />}
        <div className="pl-10">
          <CopilotTenderResultsCard
            message={message}
            activeTenderId={activeTenderId}
            onOpenTender={onOpenTender}
            onProjectCreated={onTenderProjectCreated}
          />
        </div>
      </div>
    );
  }

  if (role === "requirements") {
    return (
      <div className="animate-copilot-rise motion-reduce:animate-none">
        {opensTurn && <SensaHeader />}
        <div className="pl-10">
          <CopilotRequirementsCard message={message} />
        </div>
      </div>
    );
  }

  // A bookkeeping row, not a message. The page reads it; nothing draws it.
  if (role === "proposalFlow") return null;

  /* A step of the proposal flow, living in the thread.
   *
   * These used to render after the message list, mutually exclusive on `step`,
   * so exactly one existed and each replaced the last - a wizard pinned to the
   * bottom of a chat. As rows they behave like everything else in the
   * conversation: they arrive in order, they stay where they happened, and
   * anything typed afterwards lands beneath them.
   *
   * Resolved rows collapse to a single line rather than vanishing. A decision
   * already made should still be visible, but should not go on occupying a
   * card's worth of screen once it is no longer a decision. */
  if (role === "proposalCard") {
    if (message.resolved) {
      return (
        <>
          {opensTurn && <SensaHeader />}
          <ProposalStepRow message={message.summary || {}} onOpenSection={flow?.onOpenSection} />
        </>
      );
    }

    const f = flow || {};
    const card = {
      approach: <ProposalApproachCard message={message} onChoose={f.onApproachChosen} />,
      clarify: <ProposalClarifyCard message={message} onDone={f.onClarifyDone} />,
      oneGo: (
        <ProposalOneGoCard message={message} onDone={f.onOneGoDone} onFailed={f.onOneGoFailed} />
      ),
      plan: <ProposalPlanCard message={message} onReady={f.onPlanReady} onFailed={f.onPlanFailed} />,
      outline: (
        <ProposalOutlineCard message={message} onConfirm={f.onOutlineConfirm} onRebuild={f.onRebuild} />
      ),
      writing: (
        <ProposalWritingCard
          message={message}
          onDone={f.onSectionWritten}
          onFailed={f.onSectionFailed}
          docOpen={f.docOpen}
          onOpenDoc={f.onOpenDoc}
        />
      ),
      sectionDone: (
        <ProposalSectionCard
          message={message}
          onApprove={f.onSectionApproved}
          onRewrite={f.onSectionRewrite}
          onAutoContinue={f.onAutoContinue}
          docOpen={f.docOpen}
          onOpenDoc={f.onOpenDoc}
        />
      ),
      template: <ProposalTemplateCard message={message} onChoose={f.onTemplateChosen} />,
      complete: (
        <ProposalCompleteCard message={message} docOpen={f.docOpen} onOpenDoc={f.onOpenDoc} />
      ),
    }[message.kind];

    if (!card) return null;

    /* The rating controls for the message this card belongs to, rendered here
     * so they sit at the END of the utterance rather than in the middle of it.
     *
     * Only on the LAST card of a run — with two cards back to back the icons
     * would otherwise appear between them — and only when the message actually
     * said something, since there is nothing to copy or rate otherwise. */
    /* Which reply's controls land here — if any — is decided by the page, which
     * can see the whole run. This row only decides WHETHER now is the moment. */

    /* Not while the card is still asking something.
     *
     * Rating controls under a card awaiting an answer say "this exchange is
     * over" while it plainly is not — and they appeared under a design chooser
     * that had not been used yet. A card is finished when it has been resolved
     * (it collapses to a line) or when it is `complete`, which asks nothing and
     * is the end of the run. Everything else is still in flight. */
    const settled = message.resolved || message.kind === "complete";

    return (
      <div className="animate-copilot-rise motion-reduce:animate-none">
        {opensTurn && <SensaHeader />}
        <div className="pl-10">
          {card}
          {feedbackFor && settled && <FeedbackRow message={feedbackFor} text={feedbackFor.text} />}
        </div>
      </div>
    );
  }


  /* A restored trace: the same collapsed summary the live turn leaves behind,
     rebuilt from the row saved with it. It is an account of work rather than
     something Sensa said, so it carries no avatar of its own MID-turn.

     When it OPENS the turn it must still carry the header, because live the
     summary renders underneath the Sensa label (inside the assistant row). On
     restore it is a row of its own, so without this the collapsed "N steps"
     chip renders above the name — the turn appears to start before Sensa
     speaks. */
  if (role === "trace") {
    if (!message.steps?.length) return null;
    return (
      <div>
        {opensTurn && <SensaHeader />}
        <div className="pl-10">
          <CopilotReasoningSummary trace={{ steps: message.steps }} />
        </div>
      </div>
    );
  }

  /* History of the flow. No avatar and no bubble PER ROW: these are a log of
   * what happened, not things Sensa said, and giving each one a speaker would
   * make twelve of them read as twelve messages.
   *
   * The RUN still needs one, though. A proposal started from the setup card
   * runs entirely on the client — no model turn, so no reply text — and the
   * reply text is what normally carries the name. Live that went unnoticed
   * because a card was visibly working; restored, the run came back as a few
   * bare bullet lines under the user's own message, attributed to nobody.
   * `opensTurn` marks the first row of a run that has nothing above it already
   * speaking for it. */
  if (role === "proposalStep") {
    return (
      <>
        {opensTurn && <SensaHeader />}
        <ProposalStepRow message={message} onOpenSection={flow?.onOpenSection} />
        {/* Last row of the run: the reply's controls belong here, after
            everything it produced, rather than above these lines. */}
        {feedbackFor && (
          <div className="pl-10">
            <FeedbackRow message={feedbackFor} text={feedbackFor.text} />
          </div>
        )}
      </>
    );
  }

  if (role === "proposalSetup") {
    return (
      <div className="animate-copilot-rise motion-reduce:animate-none">
        {opensTurn && <SensaHeader />}
        <div className="pl-10">
          <ProposalSetupCard
            message={message}
            onGenerate={onProposalStart}
            carry={proposalCarry}
            onCarry={onProposalCarry}
          />
        </div>
      </div>
    );
  }

  if (role === "user") {
    return (
      <div className="flex justify-end animate-copilot-rise motion-reduce:animate-none">
        {/* Depth instead of saturation.
         *
         * It was a flat block of the brand colour under one hard coloured glow,
         * which is what made a three-word question look like a slab. Now: a
         * short gradient so light falls from the top-left, a hairline inset
         * highlight to give the edge definition, and a two-part shadow — a tight
         * contact shadow plus a wide soft one — rather than a single blur doing
         * both jobs badly.
         *
         * The ramp stops at #A64E9D. The next step up the brand scale (#B85BAE)
         * drops white text to 4.08:1, and a gradient is only as legible as its
         * brightest point. */}
        <div className="max-w-[78%] whitespace-pre-wrap rounded-[20px] rounded-br-[7px] bg-gradient-to-br from-[#A64E9D] to-[#8E4187] px-[17px] py-2.5 text-[15.5px] leading-relaxed tracking-[-0.005em] text-white ring-1 ring-inset ring-white/[0.14] shadow-[0_1px_2px_rgba(24,16,28,0.08),0_10px_24px_-12px_rgba(142,65,135,0.55)]">
          {text}
        </div>
      </div>
    );
  }

  if (role === "error") {
    return (
      <div className="flex gap-3 animate-copilot-rise motion-reduce:animate-none">
        <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-[#FEF2F2] text-[#DC2626]">
          <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M12 8v5M12 16.5v.01" />
            <circle cx="12" cy="12" r="9" />
          </svg>
        </span>
        <p role="alert" className="min-w-0 pt-1 text-[15px] leading-relaxed text-[#B91C1C]">{text}</p>
      </div>
    );
  }

  return (
    <div
      className={`group/turn flex gap-3 animate-copilot-rise motion-reduce:animate-none ${
        // Pull back into the card above it. The list spaces turns generously,
        // which is right between turns and far too much inside one.
        continuesCard ? "-mt-4" : ""
      }`}
    >
      {/* Keeps the text aligned to the same column without a second avatar.
       *
       * Driven by opensTurn, not by whatever sits directly above: a reply that
       * continues an utterance already introduced must not put the name on
       * screen a second time. */}
      {opensTurn ? <CopilotAvatar /> : <span className="w-7 flex-none" aria-hidden="true" />}
      <div className="min-w-0 flex-1">
        {opensTurn && (
          <div className="mb-1 text-[13px] font-semibold text-[#6B6472]">Sensa</div>
        )}
        {/* Sits above the answer, collapsed, so the work stays inspectable later
            without competing with the thing you actually came for. */}
        {!streaming && !tracedAboveCard && <CopilotReasoningSummary trace={message.trace} />}
        {/* The turn hands over the passages WITH the number, so the page never
            has to know a citation comes out of a trace. */}
        <CopilotMessage
          /* `shown` while a narration line types itself; identical to `text`
             otherwise. The caret follows it, so a line written here looks the
             same as one arriving from the server. */
          text={shown}
          streaming={streaming || typing || waiting}
          sources={sources}
          onCite={(n) => onCite?.(n, sources)}
        />
        {/* Always visible, not hover-revealed.
            Hidden actions are actions nobody uses: rating an answer is only
            worth asking for at the moment it is read, and a control you have to
            discover by sweeping the mouse is one most people never find at all.
            They are quiet enough — 11.5px, muted — to sit under prose without
            turning the reply into a card with a toolbar. */}
        {/* Deferred when a flow card follows: the card is part of the same
            utterance, and rating controls wedged between the sentence and the
            card it introduces read as the end of the turn when it has not
            ended. The card renders them instead — see FeedbackRow below. */}
        {/* `text`, not `shown`: copy takes the whole line even mid-reveal.
            Withheld while typing for the same reason it is withheld while
            streaming — controls that say "finished" must not appear first. */}
        {!streaming && !typing && feedbackFor === message && (
          <FeedbackRow message={message} text={text} />
        )}
      </div>
    </div>
  );
};

export default CopilotTurn;
